import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AppointmentsCardsComponent } from '../components/appointments-cards/appointments-cards.component';
import { AppointmentsToolbarComponent } from '../components/appointments-toolbar/appointments-toolbar.component';
import { AppointmentsTableComponent } from '../components/appointments-table/appointments-table.component';
import { AppointmentsCalendarComponent } from '../components/appointments-calendar/appointments-calendar.component';
import { ScheduleAppointmentModalComponent } from '../components/schedule-appointment-modal/schedule-appointment-modal.component';
import { CommonModule } from '@angular/common';
import { ProfileService } from '../services/profile.service';
import { PackageService } from '../services/package.service';
import { AppointmentService } from '../services/appointment.service';
import { BaseAppointmentsComponent } from './base-appointments.component';

/**
 * Displays and manages appointments for a specific staff member, providing both list and calendar views with filtering, toolbar controls, and integration with shared appointment services.
 * @component
 * @selector app-staff-appointments
 * @example
 *   <app-staff-appointments [staffId]="sampleStaffId" (appointmentSelected)="onAppointmentSelected($event)"></app-staff-appointments>
 * @input {string} staffId - Identifier of the staff member whose appointments this component should load and display.
 * @output {EventEmitter<Appointment>} appointmentSelected - Emits when an appointment is selected from the list or calendar; payload is the selected appointment object.
 *
 * Key methods:
 * - onViewChanged(view: 'list' | 'calendar'): void - Switches the visible view between 'list' and 'calendar'. When called, it updates the currentView and resets currentLocation and currentLocationLabels to their defaults.
 * - Inherited methods from BaseAppointmentsComponent: handles loading and refreshing appointments, applying filters, interacting with ProfileService, PackageService and AppointmentService, and common appointment CRUD flows.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Initializes component state and triggers any initial appointment data loading (defined by the BaseAppointmentsComponent and extended here).
 * - ngOnDestroy(): Performs cleanup such as unsubscribing from observables and releasing resources when the component is destroyed.
 */
@Component({
  selector: 'app-staff-appointments',
  standalone: true,
  imports: [
    AppointmentsCardsComponent,
    AppointmentsToolbarComponent,
    AppointmentsTableComponent,
    AppointmentsCalendarComponent,
    ScheduleAppointmentModalComponent,
    CommonModule,
  ],
  templateUrl: './appointments-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './staff-appointments.component.scss',
})
export class StaffAppointmentsComponent
  extends BaseAppointmentsComponent
  implements OnInit, OnDestroy
{
  readonly isAdminUser = false;

  constructor(
    profileService: ProfileService,
    packageService: PackageService,
    appointmentService: AppointmentService
  ) {
    super(profileService, packageService, appointmentService);
  }

  override onViewChanged(view: 'list' | 'calendar'): void {
    this.currentView = view;
    this.currentLocation = '';
    this.currentLocationLabels = [];
  }
}
